import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { toast, Modal, ModalFooter } from '@grandvisionhq/common';
import { Util, CheckboxWithLabel } from '@grandvisionhq/forms';
import { H2, Alert } from '@grandvisionhq/iris-ui';
import { useIntl } from '@grandvisionhq/state';
import { TitleWithIcon } from '../../../../components/TitleWithIcon/TitleWithIcon.js';
import 'throttle-debounce';
import '@grandvisionhq/carts-v2';
import { useFormSlotWithoutRedux } from '../../../../slots/forms/form-context.js';
import { useBaseSummarySlot } from '../../../../slots/summaries/summary-context.js';
import '../../../../slots/summaries/types.js';
import { useCheckoutSettings } from '../../../../context/CheckoutSettings/context.js';
import { pushLoginExpressEditUserDetailsClickEventToDataLayer } from '../../../../data-layer.js';
import { seedShippingForm, seedInitialFormState } from '../../../../StepShipping/mappers/seed-shipping-form.js';
import { useLoggedInCheckout } from '../../../context/context.js';
import '@grandvisionhq/lib-utils-w3c';
import { formStatesToValues } from '../../../utils/form-state-to-values.js';
import { isFormDataValid } from '../../../utils/validate-form-data.js';

const AddressSummary = ({ address , onSubmit , isPickupOrder =false , isOrderDetailsValid , deliveryAddress: deliveryAddressDetails , handleAddressEqual , isAddressesEqual  })=>{
    const { getLabel , country  } = useIntl();
    const labels = {
        title: getLabel('checkout.generic.title'),
        save: getLabel('common.actions.save')
    };
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const addressFormRef = React.useRef(null);
    const { formFieldsMap , shipping  } = useCheckoutSettings();
    const { isUserLoggedIn  } = useLoggedInCheckout();
    const [billingAddressFormData, setBillingAddressFormData] = React.useState({
        formState: {},
        formName: '',
        formValid: false
    });
    const [deliveryAddressFormData, setDeliveryAddressFormData] = React.useState({
        formState: {},
        formName: '',
        formValid: false
    });
    const { Slots  } = useBaseSummarySlot();
    // form fields meta data to prepare prefill data
    const fieldMapForBillingAddress = React.useMemo(()=>{
        var ref;
        return (formFieldsMap === null || formFieldsMap === void 0 ? void 0 : (ref = formFieldsMap.billingAddress) === null || ref === void 0 ? void 0 : ref.reduce((acc, item)=>{
            acc.push(item.field);
            return acc;
        }, [])) || [];
    }, [
        formFieldsMap === null || formFieldsMap === void 0 ? void 0 : formFieldsMap.billingAddress
    ]);
    const fieldMapForDeliveryAddress = React.useMemo(()=>{
        var ref;
        return (formFieldsMap === null || formFieldsMap === void 0 ? void 0 : (ref = formFieldsMap.deliveryAddress) === null || ref === void 0 ? void 0 : ref.reduce((acc, item)=>{
            acc.push(item.field);
            return acc;
        }, [])) || [];
    }, [
        formFieldsMap === null || formFieldsMap === void 0 ? void 0 : formFieldsMap.deliveryAddress
    ]);
    // Seed data into form based on form meta data
    const filledBillingFormData = React.useMemo(()=>fieldMapForBillingAddress && address && seedShippingForm(fieldMapForBillingAddress, address)
    , [
        address,
        fieldMapForBillingAddress
    ]);
    const filledShippingFormData = React.useMemo(()=>{
        if (fieldMapForDeliveryAddress && deliveryAddressDetails) {
            return seedShippingForm(fieldMapForDeliveryAddress, deliveryAddressDetails);
        }
        return fieldMapForDeliveryAddress ? seedInitialFormState(fieldMapForDeliveryAddress) : undefined;
    }, [
        deliveryAddressDetails,
        fieldMapForDeliveryAddress
    ]);
    // Address validator based on checkoutSettings.shippingFormFields configuration
    const isAddressComplete = React.useMemo(()=>{
        var ref;
        if ((formFieldsMap === null || formFieldsMap === void 0 ? void 0 : (ref = formFieldsMap.billingAddress) === null || ref === void 0 ? void 0 : ref.length) && !address || !isAddressesEqual && !deliveryAddressDetails) {
            return false;
        } else if (isAddressesEqual) {
            var ref1;
            return (formFieldsMap === null || formFieldsMap === void 0 ? void 0 : formFieldsMap.billingAddress) ? (ref1 = isFormDataValid(formFieldsMap === null || formFieldsMap === void 0 ? void 0 : formFieldsMap.billingAddress, address)) !== null && ref1 !== void 0 ? ref1 : false : false;
        } else {
            var ref2;
            return (formFieldsMap === null || formFieldsMap === void 0 ? void 0 : formFieldsMap.deliveryAddress) ? (ref2 = isFormDataValid(formFieldsMap === null || formFieldsMap === void 0 ? void 0 : formFieldsMap.deliveryAddress, deliveryAddressDetails)) !== null && ref2 !== void 0 ? ref2 : false : false;
        }
    }, [
        address,
        isAddressesEqual,
        deliveryAddressDetails,
        formFieldsMap === null || formFieldsMap === void 0 ? void 0 : formFieldsMap.billingAddress,
        formFieldsMap === null || formFieldsMap === void 0 ? void 0 : formFieldsMap.deliveryAddress, 
    ]);
    // submit callback for user to save address from modal
    const handleSubmit = React.useCallback(()=>{
        var ref, ref3;
        if (isAddressesEqual && !billingAddressFormData.formValid || !isAddressesEqual && (!billingAddressFormData.formValid || !deliveryAddressFormData.formValid)) {
            Util.focusToTopErrorFieldOnScreen();
            return;
        }
        const updatedBillingAddress = billingAddressFormData.formState && formStatesToValues(billingAddressFormData.formState);
        const updatedDeliveryAddress = deliveryAddressFormData.formState && formStatesToValues(deliveryAddressFormData.formState);
        const formData = {};
        if (billingAddressFormData.formValid) {
            formData.billingAddress = updatedBillingAddress;
        }
        if (!isAddressesEqual && deliveryAddressFormData.formValid) {
            formData.deliveryAddress = updatedDeliveryAddress;
        }
        if ((shipping === null || shipping === void 0 ? void 0 : shipping.requiredShippingCountry) && Boolean((ref = formData.billingAddress) === null || ref === void 0 ? void 0 : ref.country) && (shipping === null || shipping === void 0 ? void 0 : shipping.requiredShippingCountry) !== ((ref3 = formData.billingAddress) === null || ref3 === void 0 ? void 0 : ref3.country)) {
            toast.failed('checkout.address.requiredShippingCountry.errorMessage');
        } else {
            onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(formData);
            setIsModalOpen(false);
        }
    }, [
        isAddressesEqual,
        billingAddressFormData,
        deliveryAddressFormData.formState,
        deliveryAddressFormData.formValid,
        onSubmit,
        shipping === null || shipping === void 0 ? void 0 : shipping.requiredShippingCountry, 
    ]);
    // change handler to update local state with form data
    const onChangeHandler = React.useCallback((formData)=>{
        if (formData.formName == 'billingAddress') {
            var ref;
            setBillingAddressFormData(formData);
            if (formData.formState && ((ref = formData.formState.country) === null || ref === void 0 ? void 0 : ref.value) && formData.formState.country.value !== country) {
                handleAddressEqual(false);
            }
        }
        if (formData.formName == 'deliveryAddress') setDeliveryAddressFormData(formData);
    }, [
        country,
        handleAddressEqual
    ]);
    const onEditHandler = React.useCallback(()=>{
        pushLoginExpressEditUserDetailsClickEventToDataLayer({
            editType: isAddressComplete ? 'edit_address' : 'add_address_alert',
            isLoggedIn: isUserLoggedIn
        });
        setIsModalOpen(true);
    }, [
        isAddressComplete,
        isUserLoggedIn
    ]);
    const { billingAddress: billingAddressForm , deliveryAddress: deliveryAddressForm  } = useFormSlotWithoutRedux({
        formInitialData: swcHelpers.objectSpread({}, (filledBillingFormData === null || filledBillingFormData === void 0 ? void 0 : filledBillingFormData.formData) ? {
            billingAddress: filledBillingFormData === null || filledBillingFormData === void 0 ? void 0 : filledBillingFormData.formData
        } : {}, (filledShippingFormData === null || filledShippingFormData === void 0 ? void 0 : filledShippingFormData.formData) ? {
            deliveryAddress: filledShippingFormData === null || filledShippingFormData === void 0 ? void 0 : filledShippingFormData.formData
        } : {}),
        className: 'login-express-checkout__form-modal__form',
        formRef: addressFormRef,
        onChange: onChangeHandler
    });
    if (!Slots.addresses) return null;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Modal, {
        isOpen: isModalOpen,
        onModalClose: ()=>setIsModalOpen(false)
        ,
        showIcon: true,
        className: "login-express-checkout__form-modal",
        footer: /*#__PURE__*/ React.createElement(ModalFooter, {
            ctaCaption: getLabel('common.actions.save'),
            onCta: handleSubmit,
            onCancel: ()=>setIsModalOpen(false)
        })
    }, /*#__PURE__*/ React.createElement(H2, {
        variant: "body-head"
    }, labels.title), /*#__PURE__*/ React.createElement(React.Fragment, null, billingAddressForm), !isPickupOrder && /*#__PURE__*/ React.createElement(CheckboxWithLabel, {
        id: "usingDeliveryAddress",
        value: "usingDeliveryAddress",
        checked: Boolean(isAddressesEqual),
        onChange: (state)=>handleAddressEqual(state.checked)
        ,
        className: "billing-and-delivery-form__checkbox",
        label: getLabel('checkout.shipment.differentDeliveryAddress')
    }), !isAddressesEqual && !isPickupOrder && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(TitleWithIcon, {
        icon: "location-pin",
        label: getLabel('checkout.deliveryAddress')
    }), deliveryAddressForm)), /*#__PURE__*/ React.createElement(Slots.addresses, {
        actions: {
            onEdit: onEditHandler
        },
        values: {
            isAddressesEqual,
            isPickupOrder,
            billingAddress: address,
            deliveryAddress: deliveryAddressDetails
        },
        errorNotification: !isAddressComplete ? /*#__PURE__*/ React.createElement(Alert, {
            className: "alert-m-default ",
            variant: isOrderDetailsValid ? 'info' : 'error',
            title: getLabel('checkout.address.incomplete.title')
        }, getLabel('checkout.information.incomplete.message')) : null
    }));
};

export { AddressSummary };
//# sourceMappingURL=AddressSummary.js.map
